import React, {useState} from 'react';
import {Link} from 'gatsby';

import moment from 'moment';
import 'moment/locale/nl';
import {
  ColumnFiltersState,
  createColumnHelper,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import {Filters} from '../Table/Filters';
import Pagination from '../Table/Pagination';

moment.locale('nl');

interface DataContext {
  additionalData: {
    allRegisterIndicators: Queries.node__indicator[];
  };
}

export default function PublicationCalendar({additionalData}: DataContext): React.JSX.Element {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [show, setShow] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    return row.getValue(columnId).toLowerCase().includes(value.toLowerCase());
  };

  const toggle = () => {
    setShow(!show);
  };

  const columnHelper = createColumnHelper<Queries.node__indicator>();
  const columns = [
    columnHelper.accessor('title', {
      header: 'Indicator',
      cell: info => (
        <Link to={info.row.original.path.alias} className="vl-link vl-link--bold">{info.getValue()}</Link>
      ),
      enableGlobalFilter: true,
    }),
    columnHelper.accessor('field_indicator_thema', {
      header: 'Thema',
      cell: info => info.getValue(),
      enableGlobalFilter: false,
      filterFn: (row, colId, filterValue) =>
        filterValue.indexOf(row.getValue(colId)) !== -1,
    }),
    columnHelper.accessor('field_last_update_numeric', {
      header: 'Laatste update',
      enableGlobalFilter: false,
      cell: info => info.row.original.field_indicator_last_update,
    }),
    columnHelper.accessor('field_update_delivery_numeric', {
      header: 'Volgende update',
      enableGlobalFilter: false,
      cell: info => info.row.original.field_indicator_update_delivery,
    }),
  ];

  const table = useReactTable({
    data: additionalData.allRegisterIndicators,
    columns,
    state: {sorting, columnFilters, globalFilter},
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <section className="vl-region">
      <div className="vl-layout">
        <div className="vl-grid vl-grid--is-stacked">
          <div className="vl-col--4-12 vl-u-hidden--m">
            {table.getHeaderGroups().map(headerGroup => {
              return headerGroup.headers.map(header => (
                <Filters key={header.id} column={header.column}/>
              ));
            })}
          </div>
          <div className="vl-col--8-12 vl-col--12-12--m">
            <div className="vl-u-bg-alt" style={{padding: '2.4rem'}}>
              <label className="vl-form__label" htmlFor="input-field-2">Zoek op indicator</label>
              <input
                className="vl-input-field vl-input-field--block"
                type="text"
                id="indicator"
                name="indicator"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
              <button
                className="vl-button vl-button--narrow vl-button--naked-action vl-button--icon-before vl-u-hidden vl-u-visible--m vl-u-spacer-top--xsmall"
                vl-button=""
                onClick={toggle}
              >
                <i className="vl-button__icon vl-button__icon--before vl-vi vl-vi-content-filter" aria-hidden="true"/>
                <span className="vl-button__label">Filter</span>
              </button>
              <div className={'vl-u-hidden--l vl-u-spacer-top--xsmall ' + (show ? '' : 'vl-u-hidden')}>
                {table.getHeaderGroups().map(headerGroup => (
                  <>
                    {headerGroup.headers.map(header => (
                      <Filters column={header.column} table={table} name={flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}/>
                    ))}
                  </>
                ))}
              </div>
            </div>
            <div className="vl-typography">
              <table className="vl-u-spacer-top--medium vl-data-table vl-data-table--collapsed-m">
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id} className="table-row--width-curated">
                    {headerGroup.headers.map(header => (
                      <th
                        className={`table-column--${header.id}`}
                        key={header.id}
                        data-sort-dir={[header.column.getIsSorted() as string] ?? null}
                      >
                        <span {...{
                          className: header.column.getCanSort() ? 'cursor-pointer select-none' : '',
                          onClick: header.column.getToggleSortingHandler(),
                        }}>
                          {header.isPlaceholder ? null : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <i className="icon--sort"/>
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="table-row--width-curated">
                    {row.getVisibleCells().map(cell => (
                      <td
                        className={`table-column--${cell.column.columnDef.accessorKey}`}
                        key={cell.id}
                        data-title={cell.column.columnDef.header}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
            <div className="vl-pager vl-u-spacer-top--medium">
              <Pagination table={table}/>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
